import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { BadgeColorKeys } from '@/components/ui/badge/badge.types'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { ParkingAssignmentType, ResidentParkingSpot } from '@/models'

import * as S from './residents-parking.constants.styled'
import { formatResidentParkingVehicleDetails } from './residents-parking.utils'

export const getAssignmentTypeLabels = (t: TFunction<['parking-management']>) => ({
  Resident: t('parking-management:assignmentType.resident'),
  Unassigned: t('parking-management:assignmentType.unassigned'),
  Unit: t('parking-management:assignmentType.unit'),
})

export const getAssigneeTypeOptions = (
  t: TFunction<['parking-management']>,
): SelectOption<string>[] => {
  const labels = getAssignmentTypeLabels(t)
  return [
    { label: labels.Unit, value: 'Unit' },
    { label: labels.Resident, value: 'Resident' },
    { label: labels.Unassigned, value: 'Unassigned' },
  ]
}

const getAvailabilityBadge = (
  t: TFunction<['parking-management']>,
): Record<ParkingAssignmentType, { label: string; color: BadgeColorKeys }> => {
  const labels = getAssignmentTypeLabels(t)
  return {
    Resident: { color: 'purple', label: labels.Resident },
    Unassigned: { color: 'neutral', label: labels.Unassigned },
    Unit: { color: 'blue', label: labels.Unit },
  }
}

export const getResidentsParkingColumns = (
  t: TFunction<['parking-management']>,
  assigneeColumnSettings: PersonColumnSettings,
): TableColumn<ResidentParkingSpot>[] => {
  const availabilityBadge = getAvailabilityBadge(t)

  return [
    {
      id: 'parkingSpot',
      render: (item) => (
        <Typography as="span" size="text-small" weight="medium" color="black800">
          {item.parkingSpot || '-'}
        </Typography>
      ),
      sortAscLabel: t('parking-management:residentsParking.columns.parkingSpotSortAsc'),
      sortDescLabel: t('parking-management:residentsParking.columns.parkingSpotSortDesc'),
      sortKey: 'parkingSpot',
      title: t('parking-management:residentsParking.columns.parkingSpot'),
    },
    {
      id: 'availability',
      render: (item) => {
        const config = item.assignmentType ? availabilityBadge[item.assignmentType] : null
        if (!config) {
          return (
            <Typography as="span" size="text-small" weight="medium" color="black800">
              -
            </Typography>
          )
        }
        return (
          <Badge color={config.color} variant="light" size="small">
            {config.label}
          </Badge>
        )
      },
      title: t('parking-management:residentsParking.columns.availability'),
    },
    getPersonColumn(assigneeColumnSettings, {
      getDisplay: (row) => row.assigneeDisplay,
      getPerson: (item) => {
        const primary = (item.residents ?? item.unitResidents ?? [])[0]
        if (!primary) return null
        return { ...primary, clientId: primary.id, email: primary.user, kind: 'resident' }
      },
      id: 'assignee',
      pageName: t('parking-management:residentsParking.pageName'),
      // Note: a spot can be shared by a unit's residents - the first one is shown, the rest
      // behind a "+N" badge
      renderExtra: (item) => {
        const otherAssignees = (item.residents ?? item.unitResidents ?? []).slice(1)
        if (!otherAssignees.length) return null
        return (
          <S.BadgeContainer>
            <Tooltip
              placement="bottom-start"
              trigger={
                <Badge color="blue" variant="solid" size="small">
                  +{otherAssignees.length}
                </Badge>
              }
            >
              <S.TooltipList>
                {otherAssignees.map((assignee) => (
                  <div key={assignee.id}>• {`${assignee.firstName} ${assignee.lastName}`}</div>
                ))}
              </S.TooltipList>
            </Tooltip>
          </S.BadgeContainer>
        )
      },
      source: 'parking',
      title: t('parking-management:residentsParking.columns.assignee'),
    }),
    {
      id: 'vehicleInfo',
      render: (item) => {
        const assignees = item.residents ?? item.unitResidents ?? []
        const assigneeVehicles = assignees?.flatMap((resident) => resident.vehicles ?? []) ?? []
        const allVehicles = [...item.vehicles, ...assigneeVehicles]
        const primaryVehicle = allVehicles[0]
        const otherVehicles = allVehicles.slice(1)

        if (!primaryVehicle) {
          return (
            <Typography as="span" size="text-small" weight="regular" color="black800">
              -
            </Typography>
          )
        }

        const detailsLine = formatResidentParkingVehicleDetails(primaryVehicle)

        return (
          <S.AssigneeRow>
            <S.AssigneeTwoLines>
              <Typography as="span" size="text-small" weight="regular" color="black800">
                {primaryVehicle.licensePlate || '-'}
              </Typography>
              {detailsLine !== '-' && (
                <Typography
                  as="span"
                  size="text-small"
                  weight="regular"
                  color="black800"
                  breakLines={1}
                >
                  {detailsLine}
                </Typography>
              )}
            </S.AssigneeTwoLines>
            {otherVehicles.length > 0 && (
              <S.BadgeContainer>
                <Tooltip
                  placement="bottom-start"
                  trigger={
                    <Badge color="blue" variant="solid" size="small">
                      +{otherVehicles.length}
                    </Badge>
                  }
                >
                  <S.TooltipList>
                    {otherVehicles.map((vehicle) => (
                      <div key={vehicle.id}>
                        {'• '}
                        {formatResidentParkingVehicleDetails(vehicle, { showLicensePlate: true })}
                      </div>
                    ))}
                  </S.TooltipList>
                </Tooltip>
              </S.BadgeContainer>
            )}
          </S.AssigneeRow>
        )
      },
      title: t('parking-management:residentsParking.columns.vehicleInfo'),
    },
  ]
}

export const residentsParkingQueryFiltersSchema = z.object({
  assignmentType: z.string().optional(),
  limit: z.coerce.number().optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
})

export type ResidentsParkingFilters = z.infer<typeof residentsParkingQueryFiltersSchema>

export const initialResidentsParkingFilters: ResidentsParkingFilters = {
  assignmentType: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
}
